import { StaleRecipeWriteError } from '../../../../services/recipeWriteConflict';
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Loader2, Pencil, RefreshCw, Trash2, X } from "lucide-react";
import { primaryText, greyTextColor, borderPrimary } from "../../../../styling/stylingHelper";
import { ReviewIngredient } from "../../../../types/reviewTypes";
import { CostPriceIngredient } from "../../../../services/costPrice/costPriceService";
import { SupplierLogo } from "../../../ui/SupplierLogo";
import { eur } from "../../../orderhistory/utils";
import { Platform } from "../../../../types/Platforms";
import { ReviewWriteAdapter } from "./reviewAdapter";
import { isKnownPlatform, ingredientLineCost } from "./reviewUtils";
import { useProductUsage } from "./productUsage";
import UsageBadge from "./UsageBadge";
import SwapIngredientPanel from "./SwapIngredientPanel";

interface IngredientReviewRowProps {
    ingredient: ReviewIngredient;
    ingredientIndex: number;
    /** Cost price id or subrecipe id, depending on the flow the adapter serves. */
    itemId: string;
    // The matching row from the freshly enriched analysis (carries currentPrice).
    enrichedRow?: CostPriceIngredient;
    defaultWastePercentage: number;
    adapter: ReviewWriteAdapter;
}

const ConfidenceBadge: React.FC<{ ingredient: ReviewIngredient }> = ({ ingredient }) => {
    const { t } = useTranslation();

    if (ingredient.status === 'unmatched') {
        if (ingredient.isCustom && ingredient.articleId && ingredient.platform) {
            return (
                <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    {t('costPrice.newMenuAutomation.savedCustomIngredient')}
                </span>
            );
        }
        return (
            <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                {t('costPrice.automationReview.noMatch')}
            </span>
        );
    }
    if (ingredient.status === 'exact') {
        return (
            <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                {t('costPrice.automationReview.exactBadge')}
            </span>
        );
    }

    const confidence = ingredient.confidence ?? 0;
    const label = t('costPrice.automationReview.confidence', { percent: Math.round(confidence * 100) });
    const colorClass = confidence >= 0.85
        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
        : confidence >= 0.6
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';

    return (
        <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${colorClass}`}>
            {label}
        </span>
    );
};

const IngredientReviewRow: React.FC<IngredientReviewRowProps> = ({
    ingredient,
    ingredientIndex,
    itemId,
    enrichedRow,
    defaultWastePercentage,
    adapter,
}) => {
    const { t } = useTranslation();
    const lookupUsage = useProductUsage();

    const [showSwapPanel, setShowSwapPanel] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [isEditingQuantity, setIsEditingQuantity] = useState(false);
    const [quantityInput, setQuantityInput] = useState("");
    const [isSavingQuantity, setIsSavingQuantity] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isSubrecipe = ingredient.status === 'subrecipe';
    // New-menu unmatched lines are stored custom ingredients: they can be edited,
    // removed or swapped even though no priced supplier product was matched.
    const isStoredCustom = !!(ingredient.isCustom && ingredient.articleId && ingredient.platform);
    const isUnmatched = ingredient.status === 'unmatched' && !isStoredCustom;
    const isRemoved = ingredient.reviewAction === 'removed';

    const matchedName = isSubrecipe
        ? (ingredient.subrecipeName ?? enrichedRow?.subrecipeName)
        : ingredient.productName;

    const quantityDisplay = isSubrecipe
        ? ingredient.subrecipePortionsNeeded
        : ingredient.quantityNeeded;
    const unitDisplay = isSubrecipe ? '' : (ingredient.quantityInfo?.stdUnit ?? '');

    // How often the matched product was actually ordered in the last 60 days —
    // the quickest tell for a plausible-looking but wrong match. Subrecipes,
    // custom ingredients and unmatched rows never appear on a supplier order, so
    // a "never ordered" badge would be noise there.
    const isCustom = !!ingredient.isCustom;
    const showUsage = !isSubrecipe && !isCustom && !isUnmatched && !isRemoved && !!ingredient.platform;
    const usage = showUsage
        ? lookupUsage(ingredient.platform, ingredient.articleId, ingredient.productName)
        : null;

    // Line cost from the enriched analysis — subrecipe rows included (their
    // currentPrice is the live cost of one portion unit).
    const lineCost = (isRemoved || isUnmatched)
        ? null
        : ingredientLineCost(enrichedRow, defaultWastePercentage);

    const handleRemove = async () => {
        setIsRemoving(true);
        setError(null);
        try {
            await adapter.remove(itemId, ingredient, ingredientIndex);
        } catch (err) {
            console.error('Failed to remove ingredient:', err);
            setError(err instanceof StaleRecipeWriteError ? err.message : t('costPrice.automationReview.updateFailed'));
        } finally {
            setIsRemoving(false);
        }
    };

    const startEditQuantity = () => {
        setQuantityInput(quantityDisplay !== undefined ? quantityDisplay.toString() : "");
        setIsEditingQuantity(true);
        setError(null);
    };

    const handleSaveQuantity = async () => {
        const quantity = parseFloat(quantityInput);
        if (!Number.isFinite(quantity) || quantity <= 0) return;

        setIsSavingQuantity(true);
        setError(null);
        try {
            await adapter.setQuantity(itemId, ingredient, ingredientIndex, quantity);
            setIsEditingQuantity(false);
        } catch (err) {
            console.error('Failed to update ingredient quantity:', err);
            setError(err instanceof StaleRecipeWriteError ? err.message : t('costPrice.automationReview.updateFailed'));
        } finally {
            setIsSavingQuantity(false);
        }
    };

    return (
        <div className={`rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 py-2.5 ${isRemoved ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Template name → matched product */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`text-[14px] leading-5 ${greyTextColor} truncate`}>
                        {ingredient.templateName}
                    </span>
                    <ArrowRight className={`h-3.5 w-3.5 flex-shrink-0 ${greyTextColor}`} />
                    {isUnmatched && !ingredient.reviewAction ? (
                        <span className={`text-[14px] leading-5 italic ${greyTextColor}`}>
                            {t('costPrice.automationReview.unmatchedHint')}
                        </span>
                    ) : (
                        <span className={`text-[14px] leading-5 font-semibold ${primaryText} truncate ${isRemoved ? 'line-through' : ''}`}>
                            {matchedName}
                        </span>
                    )}
                    {isSubrecipe && (
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex-shrink-0">
                            {t('costPrice.automationReview.subrecipeBadge')}
                        </span>
                    )}
                    {/* Custom ingredients are priced from their stored price, not from
                        order history — the reviewer has to be able to tell them apart. */}
                    {isCustom && !isUnmatched && (
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 flex-shrink-0">
                            {ingredient.customPlatformName
                                ? t('costPrice.automationReview.customBadgeWithSupplier', { supplier: ingredient.customPlatformName })
                                : t('costPrice.automationReview.customBadge')}
                        </span>
                    )}
                    {!isSubrecipe && !isUnmatched && ingredient.platform && isKnownPlatform(ingredient.platform) && (
                        <SupplierLogo
                            platform={ingredient.platform.toLowerCase() as Platform}
                            size="small"
                            alt={ingredient.platform}
                            className="h-4 max-w-[60px] object-contain flex-shrink-0"
                        />
                    )}
                </div>

                {/* Quantity + line cost + usage + confidence */}
                <div className={`flex items-center gap-3 flex-shrink-0 ${isStoredCustom ? 'max-w-full flex-wrap sm:max-w-none sm:flex-nowrap' : ''}`}>
                    {showUsage && <UsageBadge usage={usage} />}
                    {!isUnmatched && quantityDisplay !== undefined && (
                        isEditingQuantity && !isRemoved ? (
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    autoFocus
                                    value={quantityInput}
                                    onChange={(e) => setQuantityInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveQuantity();
                                        if (e.key === 'Escape') setIsEditingQuantity(false);
                                    }}
                                    className={`w-20 rounded-md border bg-background dark:bg-background-dark px-2 h-7 text-[13px] ${primaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                                />
                                <span className={`text-[13px] ${greyTextColor}`}>{unitDisplay}</span>
                                <button
                                    type="button"
                                    onClick={handleSaveQuantity}
                                    disabled={isSavingQuantity || !(parseFloat(quantityInput) > 0)}
                                    title={t('costPrice.automationReview.saveQuantity')}
                                    className="p-1.5 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 disabled:opacity-50"
                                >
                                    {isSavingQuantity ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditingQuantity(false)}
                                    title={t('costPrice.automationReview.cancel')}
                                    className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark ${greyTextColor}`}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={startEditQuantity}
                                disabled={isRemoved}
                                title={t('costPrice.automationReview.editQuantity')}
                                className={`flex items-center gap-1.5 text-[13px] ${primaryText} rounded-md border ${borderPrimary} bg-background dark:bg-background-dark px-2 py-1 hover:border-darkGreenColor dark:hover:border-darkGreenColor-dark disabled:hover:border-inherit disabled:cursor-default`}
                            >
                                <span>{quantityDisplay} {unitDisplay}</span>
                                {!isRemoved && <Pencil className={`h-3 w-3 ${greyTextColor}`} />}
                            </button>
                        )
                    )}
                    {lineCost !== null && (
                        <span className={`text-[13px] font-semibold ${primaryText}`}>
                            {eur(lineCost)}
                        </span>
                    )}
                    {isRemoved ? (
                        <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>
                            {t('costPrice.automationReview.removed')}
                        </span>
                    ) : (
                        <>
                            {ingredient.reviewAction === 'swapped' && (
                                <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                    {t('costPrice.automationReview.swapped')}
                                </span>
                            )}
                            {ingredient.alreadyOnDish && !ingredient.reviewAction && (
                                <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>
                                    {t('costPrice.automationReview.alreadyOnDish')}
                                </span>
                            )}
                            <ConfidenceBadge ingredient={ingredient} />
                        </>
                    )}

                    {/* Actions: keep is the default (no-op) — only remove + swap need buttons */}
                    {!isRemoved && (
                        <div className="flex items-center gap-1">
                            {!isUnmatched && (
                                <button
                                    type="button"
                                    onClick={handleRemove}
                                    disabled={isRemoving}
                                    title={t('costPrice.automationReview.remove')}
                                    className={`p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 disabled:opacity-50`}
                                >
                                    {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowSwapPanel(v => !v)}
                                title={t('costPrice.automationReview.swap')}
                                className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark ${greyTextColor}`}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Reason line (Sonnet's short justification / ai_error note) */}
            {ingredient.reason && ingredient.reason !== 'ai_error' && !isRemoved && (
                <p className={`mt-1 text-[12px] ${greyTextColor}`}>{ingredient.reason}</p>
            )}
            {ingredient.reason === 'ai_error' && (
                <p className="mt-1 text-[12px] text-red-500 dark:text-red-400">{t('costPrice.automationReview.aiErrorReason')}</p>
            )}

            {error && (
                <p className="mt-1 text-[13px] text-red-600 dark:text-red-400">{error}</p>
            )}

            {showSwapPanel && !isRemoved && (
                <SwapIngredientPanel
                    ingredient={ingredient}
                    ingredientIndex={ingredientIndex}
                    itemId={itemId}
                    defaultWastePercentage={defaultWastePercentage}
                    adapter={adapter}
                    onDone={() => setShowSwapPanel(false)}
                />
            )}
        </div>
    );
};

export default IngredientReviewRow;
